/// <reference types="vite/client" />
/**
 * The single place the app talks HTTP to Frappe.
 *
 * Screens never call this — they use the typed hooks in this folder, which call the
 * mappers in ./mappers, which call this. See docs/bosun-frappe-contract.md §2.
 *
 * Auth is the Frappe session cookie. Same-origin in dev (Vite proxies /api); in a static
 * production deploy `apiBase()` makes every URL absolute and the call is cross-origin, carrying
 * `X-Frappe-CSRF-Token` on writes.
 *
 * ---- NOT vestigial — task 12 kept this file, deliberately ----
 *
 * `getResource`/`getResourceDoc` (resource-mode `/api/resource/<DocType>` reads) are still real,
 * load-bearing dependencies for:
 *
 *   - `useItems.ts`'s `useItemsByCode`/`useItem`/`fetchItemPrices` — item-by-code and indicative
 *     price lookups behind `/rfq`, `/compare`, `/quote`, `/revisions`, `SupplierQuotationsPage`.
 *     Also the last live importer of `./mappers/item.ts`.
 *   - `useEnquiryFromMail.ts` — every picker on the "Create enquiry from this mail" dialog.
 *   - `useMailExtraction.ts`'s `verifyItemCodes`/`resolveCustomerHint` (`getResource('Item'/
 *     'Customer', …)`) — checking the AI's suggested lines against the real masters after
 *     `mail.extract_enquiry_items` answers, live or mocked.
 *
 * `createResource` (resource-mode write) has no live caller left as of the bug fix that moved
 * "Create enquiry from this mail" onto `enquiry.create_enquiry` — it used to be the write path for
 * that dialog, POSTing straight to `/api/resource/Opportunity`, an old doctype the rest of the app
 * had already moved off, which is exactly why the created document could never be read back
 * (see `useEnquiryFromMail.ts`'s own header). Left defined rather than deleted: it's a thin,
 * correct primitive (mirrors `getResourceDoc`'s response-unwrapping) that the next resource-mode
 * write can reuse without reinventing.
 *
 * `callMethod` (generic `/api/method/<whitelisted>` POST) is in the same position as of the
 * extraction-is-async fix: its last caller was `useMailExtraction.ts`'s fallback to the PROPOSED
 * `quoflow.api.extract_enquiry_from_mail`, removed because that method is confirmed NOT deployed
 * on the live bench (`module 'quoflow.api' has no attribute` — round 2, ask A5) and could not
 * rescue anything. Kept defined for the same reason `createResource` is: a correct, generic
 * primitive the contract's other `/api/method/<whitelisted>` calls (`login`, `upload_file`,
 * `email.make`, PDF) can reach for without reinventing it.
 *
 * None of the reads above have a `quoflow.*` method to move to yet. See task-12-report.md for the
 * full backend-gap list.
 */

import { apiBase } from './apiBase'
import { withSessionRetry } from './sessionRetry'
import { currentCsrfToken, proxyBearer } from './frappeSession'

export const GATEWAY = '/api/method/quoflow.quoflow.api.generic'

/**
 * Resource mode — see docs/bosun-frappe-open-questions.md Q22 and docs/bosun-frappe-contract.md
 * §1.3. The generic gateway (`GATEWAY` above) is not deployed on the live server: every
 * `quoflow.quoflow.api.generic.*` call there fails with `No module named 'quoflow.quoflow.api'`.
 * `/api/resource/<DocType>` works for every doctype, transactional ones included, so this switch
 * reroutes `getDoc`/`getList`/`createDoc`/`updateDoc` through it instead, keeping their public
 * signatures and return types unchanged.
 *
 * Read once at module load: client code cannot read `process.env`, and the mode cannot sensibly
 * change mid-session anyway. Vite inlines `VITE_`-prefixed keys from `.env` into
 * `import.meta.env` at build/serve time — no config change needed.
 */
/**
 * **Resource mode is now the DEFAULT**, opt out with `VITE_BOSUN_RESOURCE_MODE=0`.
 *
 * It was opt-in, and that was the wrong way round: the gateway does not exist on the real server,
 * so defaulting to it meant defaulting to the one transport that cannot work in production.
 *
 * The bug that forced this: "Create enquiry from this mail" writes through `/api/resource`, which
 * `vite.config.ts` routes live — but `/rfq/:id` read it back with `getDoc`, which in gateway mode
 * builds a `quoflow.quoflow.api.generic.get_document` URL that no proxy rule matches, so it fell
 * through to the mock. A freshly created `OPP-2026-…` came back "not found" from a table that had
 * never seen it. Same document, two backends — the third time this project has hit that shape
 * (mock `m1` vs live `uo5sdmvpl3`; the unproxied `email.make`).
 *
 * Flipping the default is what makes read and write agree by construction rather than by
 * remembering to add a proxy rule. `mock-server/frappe/resource.ts` now serves POST and PUT too,
 * so mock-only dev keeps working through the same URLs.
 */
const RESOURCE_MODE = import.meta.env.VITE_BOSUN_RESOURCE_MODE !== '0'

console.log(
  RESOURCE_MODE
    ? '[bosun] transport: resource mode (/api/resource — the only one the real server has, Q22)'
    : '[bosun] transport: gateway (quoflow.quoflow.api.generic) — mock only; not deployed live',
)

/** A Frappe filter triple: [fieldname, operator, value]. */
/**
 * `[field, op, value]`, or `[childDoctype, field, op, value]` to filter a parent by one of its
 * child tables — the four-element form Frappe uses for e.g.
 * `["Dynamic Link", "link_name", "=", "<customer>"]`, which is how a Customer's Contacts are
 * found (see `useCustomerContacts.ts`). Verified live 2026-08-09.
 */
export type FrappeFilter = [string, string, unknown] | [string, string, string, unknown]

export interface GatewayQuery {
  doctype: string
  filters?: FrappeFilter[]
  /** Required in practice: the gateway returns only the fields you name. */
  fields?: string[]
  limit?: number
  offset?: number
  orderBy?: string
  /**
   * Parent doctype, for reading a CHILD table directly (`/api/resource/Enquiry Item?parent=Enquiry`).
   *
   * Frappe refuses a child-table list read without it — `PermissionError: Insufficient Permission
   * for Enquiry Item` — and grants it once the parent is named, because permission is then checked
   * against the parent doctype instead. Verified live 2026-08-09: without `parent`, PermissionError;
   * with it, all 159 `Enquiry Item` rows in one request.
   *
   * This is what keeps the analytics line counts a single call instead of one read per enquiry.
   */
  parent?: string
}

/**
 * Shared filter/fields/paging/order_by params, used by both the gateway (which also carries
 * `doctype` as a query param) and `/api/resource` (which carries the doctype in the path only).
 */
function buildQueryParams(opts: Omit<GatewayQuery, 'doctype'>): URLSearchParams {
  const p = new URLSearchParams()
  if (opts.filters?.length) p.set('filters', JSON.stringify(opts.filters))
  if (opts.fields?.length) p.set('fields', JSON.stringify(opts.fields))
  if (opts.limit !== undefined) p.set('limit_page_length', String(opts.limit))
  if (opts.offset !== undefined) p.set('limit_start', String(opts.offset))
  if (opts.orderBy) p.set('order_by', opts.orderBy)
  if (opts.parent) p.set('parent', opts.parent)
  return p
}

/** Query string (no leading `?`) for get_list / get_document. */
export function buildGatewayQuery(opts: GatewayQuery): string {
  const p = new URLSearchParams()
  p.set('doctype', opts.doctype)
  for (const [k, v] of buildQueryParams(opts)) p.append(k, v)
  return p.toString()
}

/**
 * Query string (no leading `?`) for `/api/resource/<DocType>` list reads. Same params as the
 * gateway (filters/fields/paging/order_by) but the doctype is never repeated as a query param —
 * it already lives in the path.
 */
export function buildResourceQuery(opts?: Omit<GatewayQuery, 'doctype'>): string {
  return opts ? buildQueryParams(opts).toString() : ''
}

/**
 * URL-encoded `/api/resource` path: `<doctype>` or `<doctype>/<name>`. Two-word doctypes
 * (`Supplier Quotation`) must be encoded — `Supplier%20Quotation` — and must never also appear
 * as a `?doctype=` query param.
 */
export function buildResourcePath(doctype: string, name?: string): string {
  const base = `/api/resource/${encodeURIComponent(doctype)}`
  return name === undefined ? base : `${base}/${encodeURIComponent(name)}`
}

/** Frappe's `exc_type` → our stable error codes. See contract §4. */
const EXC_CODES: Record<string, string> = {
  PermissionError: 'FORBIDDEN',
  ValidationError: 'VALIDATION',
  MandatoryError: 'VALIDATION',
  DoesNotExistError: 'NOT_FOUND',
  DuplicateEntryError: 'DUPLICATE',
}

/**
 * `_server_messages` is doubly encoded: a JSON string holding an array whose elements are
 * either JSON strings or plain objects. Both occur in the wild. Never throws.
 */
export function parseServerMessages(raw: unknown): string[] {
  if (typeof raw !== 'string') return []
  let outer: unknown
  try {
    outer = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(outer)) return []
  const out: string[] = []
  for (const entry of outer) {
    let obj: unknown = entry
    if (typeof entry === 'string') {
      try {
        obj = JSON.parse(entry)
      } catch {
        out.push(entry)
        continue
      }
    }
    const message = (obj as { message?: unknown } | null)?.message
    if (typeof message === 'string') out.push(message)
  }
  return out
}

export class FrappeApiError extends Error {
  readonly status: number
  readonly code: string
  readonly excType?: string

  constructor(status: number, code: string, message: string, excType?: string) {
    super(message)
    this.name = 'FrappeApiError'
    this.status = status
    this.code = code
    this.excType = excType
  }

  static fromBody(status: number, body: unknown): FrappeApiError {
    const b = (body ?? {}) as { exc_type?: string; _server_messages?: unknown; message?: unknown }
    const excType = typeof b.exc_type === 'string' ? b.exc_type : undefined
    const code = (excType && EXC_CODES[excType]) ?? 'UNKNOWN'
    const messages = parseServerMessages(b._server_messages)
    const message =
      messages[0] ?? (typeof b.message === 'string' ? b.message : `Request failed with status ${status}`)
    return new FrappeApiError(status, code, message, excType)
  }
}

export interface Paged<T> {
  data: T[]
  total: number
  limit: number
  offset: number
}

/**
 * Gateway responses carry total_count/limit_start/limit_page_length; /api/resource carries
 * only `data`. Normalize both onto the envelope the hooks already consume.
 *
 * Real limitation, not hidden here: when there is no `total_count` (every `/api/resource` list —
 * so `getList`/`getResource` in resource mode too), `total` falls back to `data.length`, i.e. the
 * **page size**, not the full row count. Callers cannot compute "page 3 of 9" against
 * `/api/resource` from this envelope alone; `frappe.client.get_count` (contract §3) is the only
 * way to get a true total there.
 */
export function normalizeList<T>(body: unknown): Paged<T> {
  const b = (body ?? {}) as {
    data?: unknown
    total_count?: unknown
    limit_start?: unknown
    limit_page_length?: unknown
  }
  const data = Array.isArray(b.data) ? (b.data as T[]) : []
  const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback)
  return {
    data,
    total: num(b.total_count, data.length),
    offset: num(b.limit_start, 0),
    limit: num(b.limit_page_length, data.length),
  }
}

async function requestOnce<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase()
  const csrf = method !== 'GET' ? currentCsrfToken() : null
  // Identity for the production proxy; `null` in dev. See `currentClerkToken`.
  const bearer = await proxyBearer()
  const res = await fetch(apiBase() + url, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
      // CSRF and bearer last, so a caller-supplied `headers` cannot clobber either — neither is
      // the caller's to override. (No current caller passes `headers`; this keeps it that way.)
      ...(csrf ? { 'X-Frappe-CSRF-Token': csrf } : {}),
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
  })
  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = undefined
  }
  if (!res.ok) throw FrappeApiError.fromBody(res.status, body)
  return body as T
}

/**
 * This IS the residual `/api/resource` surface CLAUDE.md and the spec mean when they talk about
 * "the equivalent for `/api/resource`" — not `client.ts`, which owns the separate `/api/v1`
 * surface (see that file's own header). The final whole-branch review (finding 2) found this
 * function bare, un-retried, while `client.ts`'s already had the wrapper: nine hooks reading
 * through `getResource`/`getResourceDoc`/`getDoc`/`getList` (`useItems.ts`, `useEnquiryFromMail.ts`,
 * `useMailExtraction.ts`, `useMasters.ts`, `useUoms.ts`, `useRfqReplies.ts`, `useBoardReplies.ts`,
 * `rqJob.ts`, `useEnquiryMutations.ts`) had no recovery from an expired session at all — on
 * `/compare/:id` a `quoflow.*` call would silently recover while the adjacent item lookup hard-
 * failed. `FrappeApiError` already exposes `status` and `excType` structurally, which is all
 * `isSessionExpired` needs — no shape change required to plug in here.
 *
 * `requestOnce` above is the one bare-`fetch` entry point in this module; every exported function
 * below funnels through it (directly or via `unwrap*`), so wrapping this one function is enough —
 * there is no second fetch call in this file that also needs it.
 */
function request<T>(url: string, init?: RequestInit): Promise<T> {
  return withSessionRetry(() => requestOnce<T>(url, init))
}

/** The gateway wraps single documents as { status, data }. */
function unwrap<T>(body: unknown): T {
  return ((body ?? {}) as { data?: unknown }).data as T
}

/** `/api/resource` also wraps single documents as { data }, but tolerates a bare body too. */
function unwrapResourceDoc<T>(body: unknown): T {
  const b = (body ?? {}) as { data?: T }
  return b.data ?? (body as T)
}

/** What `request()` needs: URL plus the parts of `RequestInit` these calls ever set. */
export interface RequestSpec {
  url: string
  init?: RequestInit
}

/**
 * Pure request-spec builders, one per gateway function below, each parameterized by
 * `resourceMode` — the single source of truth for "what does this call send" in either mode.
 * `getDoc`/`getList`/`createDoc`/`updateDoc` call these with the module's `RESOURCE_MODE`;
 * `getResource`/`getResourceDoc` call the list/doc builders with `resourceMode: true` directly,
 * so there is exactly one implementation of each URL/body shape, not two. Exported so resource
 * mode is testable without stubbing `import.meta.env` — see frappe.test.ts.
 */
export function buildGetDocRequest(doctype: string, name: string, resourceMode: boolean): RequestSpec {
  if (resourceMode) return { url: buildResourcePath(doctype, name) }
  const qs = new URLSearchParams({ doctype, name }).toString()
  return { url: `${GATEWAY}.get_document?${qs}` }
}

export function buildGetListRequest(q: GatewayQuery, resourceMode: boolean): RequestSpec {
  if (resourceMode) {
    const { doctype, ...rest } = q
    const qs = buildResourceQuery(rest)
    const path = buildResourcePath(doctype)
    return { url: qs ? `${path}?${qs}` : path }
  }
  return { url: `${GATEWAY}.get_list?${buildGatewayQuery(q)}` }
}

export function buildCreateDocRequest(
  doctype: string,
  data: Record<string, unknown>,
  resourceMode: boolean,
): RequestSpec {
  if (resourceMode) {
    // /api/resource's POST body IS the document data — not wrapped in { doctype, data } like
    // the gateway, since the doctype already lives in the path.
    return { url: buildResourcePath(doctype), init: { method: 'POST', body: JSON.stringify(data) } }
  }
  return {
    url: `${GATEWAY}.create_document`,
    init: { method: 'POST', body: JSON.stringify({ doctype, data }) },
  }
}

export function buildUpdateDocRequest(
  doctype: string,
  name: string,
  data: Record<string, unknown>,
  resourceMode: boolean,
): RequestSpec {
  if (resourceMode) {
    // Same bare-body rule as createDoc. Child-table semantics are unchanged: standard Frappe
    // REST `PUT` replaces a supplied child-table array wholesale — the same "send every row you
    // intend to keep, each with its `name`" rule the gateway documents (contract §2.4) — so
    // useEnquiryMutations' read-modify-write helper stays correct in either mode.
    return { url: buildResourcePath(doctype, name), init: { method: 'PUT', body: JSON.stringify(data) } }
  }
  return {
    url: `${GATEWAY}.update_document`,
    init: { method: 'PUT', body: JSON.stringify({ doctype, name, data }) },
  }
}

export async function getDoc<T>(doctype: string, name: string): Promise<T> {
  const { url, init } = buildGetDocRequest(doctype, name, RESOURCE_MODE)
  const body = await request(url, init)
  return RESOURCE_MODE ? unwrapResourceDoc<T>(body) : unwrap<T>(body)
}

export async function getList<T>(q: GatewayQuery): Promise<Paged<T>> {
  const { url, init } = buildGetListRequest(q, RESOURCE_MODE)
  return normalizeList<T>(await request(url, init))
}

export async function createDoc<T>(doctype: string, data: Record<string, unknown>): Promise<T> {
  const { url, init } = buildCreateDocRequest(doctype, data, RESOURCE_MODE)
  return unwrap<T>(await request(url, init))
}

export async function updateDoc<T>(doctype: string, name: string, data: Record<string, unknown>): Promise<T> {
  const { url, init } = buildUpdateDocRequest(doctype, name, data, RESOURCE_MODE)
  return unwrap<T>(await request(url, init))
}

/** Master data. Note /api/resource returns a bare { data } with no count. */
export async function getResource<T>(doctype: string, q?: Omit<GatewayQuery, 'doctype'>): Promise<Paged<T>> {
  const { url } = buildGetListRequest({ doctype, ...q }, true)
  return normalizeList<T>(await request(url))
}

export async function getResourceDoc<T>(doctype: string, name: string): Promise<T> {
  const { url } = buildGetDocRequest(doctype, name, true)
  return unwrapResourceDoc<T>(await request(url))
}

/**
 * Create a document through `/api/resource/<DocType>`, unconditionally — the resource sibling of
 * `createDoc`, which follows `RESOURCE_MODE`.
 *
 * Needed because the gateway's `create_document` does not exist on the real server (Q22), so the
 * one write Bosun performs today — creating an `Opportunity` from a mail — has to go here
 * regardless of how the rest of the transport is configured.
 *
 * The response is `{ data: <the created doc> }`, with `name` assigned by the naming series, so it
 * is unwrapped the same way `getResourceDoc` unwraps a read.
 */
export async function createResource<T>(doctype: string, data: Record<string, unknown>): Promise<T> {
  const { url, init } = buildCreateDocRequest(doctype, data, true)
  return unwrapResourceDoc<T>(await request(url, init))
}

/**
 * Delete a document through `/api/resource/<DocType>/<name>` — a DRAFT supplier quotation filed
 * wrong (2026-09-19: rate 0.00 from a parser that could not read the workbook), which the server
 * refuses to update or duplicate. Frappe answers `202 {"data":"ok"}`; a submitted document is
 * refused by the server itself, so this can only remove what was never final.
 */
export async function deleteResource(doctype: string, name: string): Promise<void> {
  await request<unknown>(`${buildResourcePath(doctype)}/${encodeURIComponent(name)}`, { method: 'DELETE' })
}

export async function callMethod<T>(method: string, body?: unknown): Promise<T> {
  const init: RequestInit = body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) }
  return request<T>(`/api/method/${method}`, init)
}
